// Default content for CMS-editable static pages (About, Contact, Delivery, FAQ).
// Each public page renders `settings.<key>` when an admin has saved content for it,
// falling back to these defaults otherwise — so the site never breaks before first save.

export const defaultAboutContent = {
  hero: {
    title: 'About Prime Mattresses',
    subtitle: "Crafting exceptional sleep experiences since 2009. We believe everyone deserves a great night's sleep.",
  },
  stats: [
    { value: '15+', label: 'Years Experience' },
    { value: '50K+', label: 'Happy Customers' },
    { value: '4.9', label: 'Average Rating' },
    { value: '100%', label: 'UK Made' },
  ],
  story: {
    heading: 'Our Story',
    paragraphs: [
      "Prime Mattresses was born from a simple frustration: why was it so hard to find a mattress that truly delivered on its promises? After years of disappointing purchases and sleepless nights, our founder James Harrison set out to create something better.",
      "Starting in a small workshop in Manchester, we combined traditional British craftsmanship with cutting-edge sleep technology. The result? Mattresses that don't just meet expectations—they exceed them.",
      "Today, we've helped over 50,000 customers discover better sleep. But our mission remains the same: to create the world's most comfortable, supportive, and innovative mattresses.",
    ],
    image: 'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=600',
  },
  valuesHeading: 'Our Values',
  valuesSubtitle: 'The principles that guide everything we do',
  values: [
    { icon: 'Award', title: 'Quality First', description: 'We never compromise on materials or craftsmanship. Every mattress is built to last.' },
    { icon: 'Users', title: 'Customer Focus', description: "Your sleep satisfaction is our priority. We're here to help you find your perfect match." },
    { icon: 'Heart', title: 'Sleep Health', description: "We're passionate about improving lives through better sleep science and innovation." },
    { icon: 'Leaf', title: 'Sustainability', description: 'Committed to eco-friendly practices and sustainable manufacturing processes.' },
  ],
  teamHeading: 'Meet The Team',
  teamSubtitle: 'The passionate people behind Prime Mattresses',
  team: [
    { name: 'James Harrison', role: 'Founder & CEO', image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop' },
    { name: 'Dr. Sarah Mitchell', role: 'Sleep Scientist', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=300&fit=crop' },
    { name: 'Michael Chen', role: 'Head of Design', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop' },
  ],
  cta: {
    heading: 'Ready for Better Sleep?',
    subtitle: "Join over 50,000 happy customers who've transformed their sleep with Prime.",
    buttonText: 'Shop Mattresses',
    buttonLink: '/mattresses',
  },
};

export const defaultContactContent = {
  hero: {
    title: 'Get In Touch',
    subtitle: "We're here to help with any questions about our mattresses, orders, or sleep advice.",
  },
  liveChat: {
    value: 'Start a Chat',
    sub: 'Available during business hours',
  },
  officeName: 'Prime Mattresses HQ',
  businessHours: ['Mon–Fri: 9am – 6pm', 'Sat: 10am – 4pm', 'Sun: Closed'],
  quickFaqs: [
    { q: 'How long does delivery take?', a: 'Standard delivery is 5–7 working days. Express (2–3 days) is available for £49.' },
    { q: 'Can I try the mattress first?', a: "Yes — our 200-night home trial lets you return it for a full refund, no questions asked." },
    { q: 'How do I return a mattress?', a: "Contact us and we'll arrange a free collection at a time that suits you." },
  ],
};

export const defaultDeliveryContent = {
  hero: {
    title: 'Free UK Delivery',
    subtitle: "Every Prime mattress comes with free delivery, room of choice setup, and old mattress removal. It's all part of the service.",
  },
  benefits: [
    { icon: 'Truck', title: '100% Free', description: 'Free delivery on all mattresses to mainland UK' },
    { icon: 'MapPin', title: 'Room of Choice', description: "We'll deliver and set up in any room" },
    { icon: 'Recycle', title: 'Old Mattress Removal', description: 'Free collection and recycling of your old mattress' },
    { icon: 'Clock', title: 'Choose Your Slot', description: 'Select a convenient 2-hour delivery window' },
  ],
  steps: [
    { icon: 'Package', title: 'Order Placed', description: 'Place your order online and receive instant confirmation' },
    { icon: 'Calendar', title: 'Book Delivery', description: 'Choose a convenient 2-hour delivery slot' },
    { icon: 'Truck', title: 'Delivery Day', description: 'Track your delivery in real-time' },
    { icon: 'Users', title: 'Room Setup', description: 'Our team sets up in your room of choice' },
  ],
  deliveryTimes: [
    { title: 'Standard Delivery', sub: '5-7 working days', free: true, price: '' },
    { title: 'Express Delivery', sub: '2-3 working days', free: false, price: '£49' },
    { title: 'Remote Areas', sub: 'Add 2-3 working days', free: true, price: '' },
  ],
  includedItems: [
    'Two-person delivery team',
    'Delivery to your room of choice',
    'Mattress unpacking and setup',
    'Packaging removal and recycling',
    'Old mattress removal (optional)',
    '2-hour delivery time slot',
    'Real-time tracking updates',
  ],
  coverage: {
    heading: 'UK-Wide Coverage',
    description: "We deliver to every corner of the United Kingdom. Whether you're in central London or the Scottish Highlands, we'll get your mattress to you.",
    regions: ['England', 'Scotland', 'Wales', 'Northern Ireland', 'Isle of Man', 'Channel Islands'],
    note: 'Remote areas add 2-3 days',
  },
  faqs: [
    { q: 'How long does delivery take?', a: 'Standard delivery is 5-7 working days. Express delivery (2-3 days) is available for £49.' },
    { q: 'Do you deliver to my area?', a: 'We deliver throughout the UK. Mainland UK gets free delivery. Scottish Highlands, Islands, and Northern Ireland may have slightly longer delivery times (add 2-3 days).' },
    { q: 'Can I change my delivery date?', a: 'Yes, you can reschedule up to 48 hours before your delivery date via the link in your confirmation email or by contacting us.' },
    { q: "What if I'm not home?", a: 'Someone over 18 must be present to accept the delivery. If you need to reschedule, just let us know.' },
    { q: 'Will my mattress fit through the door?', a: 'Our mattresses are vacuum-packed and rolled for easy delivery. They fit through standard doorways easily. Once unwrapped, they expand to full size within 4-6 hours.' },
  ],
  cta: {
    heading: 'Ready to Order?',
    subtitle: 'Free delivery, free setup, free old mattress removal.',
  },
};

export const defaultFaqContent = {
  hero: {
    title: 'Frequently Asked Questions',
    subtitle: 'Find answers to common questions about our mattresses, delivery, and more.',
  },
  faqs: [
    { id: 1, category: 'delivery', q: 'How long does delivery take?', a: "Standard delivery is 5–7 working days from order confirmation. Express delivery (2–3 working days) is available for an additional £49. You'll receive email updates at each stage." },
    { id: 2, category: 'delivery', q: 'Is delivery free?', a: 'Yes! We offer free standard delivery on all mattresses to mainland UK addresses. Delivery to Scottish Highlands, Islands, Northern Ireland, and remote areas may take 2–3 additional days.' },
    { id: 3, category: 'delivery', q: 'Will you take my old mattress away?', a: 'Yes, we offer free old mattress removal with every delivery. Select this option at checkout and our delivery team will take your old mattress away for responsible recycling.' },
    { id: 4, category: 'delivery', q: 'Do I need to be home for delivery?', a: "Yes — as we deliver to your room of choice and set up the mattress, someone over 18 must be present. We'll give you a 2-hour delivery window the day before." },
    { id: 5, category: 'delivery', q: 'Can I change my delivery date?', a: 'Yes, you can reschedule up to 48 hours before the scheduled date. Contact our customer service team or use the link in your confirmation email.' },
    { id: 6, category: 'returns', q: 'What is the 200-night trial?', a: "Our 200-night trial gives you over 6 months to try your mattress at home. If you're not completely satisfied, contact us and we'll arrange a free collection and full refund. No questions asked." },
    { id: 7, category: 'returns', q: 'How do I return a mattress?', a: "Contact our customer service team and we'll arrange a free collection at a time that suits you. Once collected, your refund is processed within 5–7 working days." },
    { id: 8, category: 'returns', q: 'Is there a restocking fee?', a: "No. There are absolutely no fees for returns within the trial period. We'll collect your mattress free of charge and issue a complete refund." },
    { id: 9, category: 'returns', q: 'What happens to returned mattresses?', a: 'Returned mattresses in good condition are donated to charity or recycled responsibly. We never resell returned mattresses.' },
    { id: 10, category: 'payment', q: 'What payment methods do you accept?', a: 'We accept all major credit and debit cards (Visa, Mastercard, American Express), PayPal, Apple Pay, Google Pay, and Klarna for buy-now-pay-later.' },
    { id: 11, category: 'payment', q: 'Do you offer finance options?', a: 'Yes, we offer 0% interest finance through Klarna. Spread the cost over 6–36 months with no interest. Subject to status and approval.' },
    { id: 12, category: 'payment', q: 'When will I be charged?', a: 'Your card is charged immediately upon order confirmation. For finance orders, payments begin according to your chosen payment plan.' },
    { id: 13, category: 'payment', q: 'Is my payment secure?', a: 'Absolutely. We use industry-standard SSL encryption and are PCI DSS compliant. Your payment details are never stored on our servers.' },
    { id: 14, category: 'product', q: 'What is the 10-year guarantee?', a: 'Our 10-year guarantee covers manufacturing defects including sagging greater than 25mm, broken springs, and cover defects. Normal wear and tear is not covered.' },
    { id: 15, category: 'product', q: 'How firm are your mattresses?', a: 'Our mattresses range from medium (5/10) to medium-firm (6/10). Take our quiz for a personalised recommendation.' },
    { id: 16, category: 'product', q: 'Are your mattresses suitable for bad backs?', a: 'Yes, our pocket spring technology provides excellent spinal support. Many customers with back pain report significant improvement. Medium-firm options are particularly recommended.' },
    { id: 17, category: 'product', q: 'Do I need a specific bed frame?', a: 'Our mattresses work with most bed frames including divans, platform beds, slatted bases, and adjustable beds. Slats should be no more than 7cm apart.' },
    { id: 18, category: 'product', q: 'How do I care for my mattress?', a: 'Rotate 180° every 3–6 months. Use a mattress protector to guard against spills. Avoid folding or bending the mattress. Vacuum occasionally to remove dust.' },
  ],
};

export const defaultHomeContent = {
  hero: {
    pretitle: 'Mega Sleep',
    ratingText: '380,000+ five-star reviews',
  },
  shopByProduct: {
    heading: 'Shop By Product',
  },
  quizBanner: {
    heading: 'Need help choosing a mattress?',
    buttonText: 'Try Our Mattress Quiz',
    buttonLink: '/quiz',
    image: '/mattresses.webp',
  },
  tech: {
    heading: 'Prime Technology',
    video: '/pm.mp4',
    items: [
      { icon: 'Layers', title: 'Aerocoil® Foam', description: 'Our unique patented foam maximises airflow for unmatched bounce.' },
      { icon: 'Thermometer', title: 'Stratos® cool-touch', description: 'Stratos uses thermo-reactive fibres with air-flow weaving.' },
      { icon: 'CircleDot', title: 'Aerosoft® Springs', description: "Patented springs respond to your body's unique support needs." },
      { icon: 'Feather', title: 'Adjustable Pillow', description: 'Customisable pillows for your perfect height and firmness.' },
    ],
  },
  bestSellers: {
    heading: 'Best Sellers',
    fallbackItems: [
      { name: 'Prime® Pro Mattress', price: 'from £779', wasPrice: 'was £1199', image: '/quiz.webp', link: '/mattresses', allLink: '/mattresses', allLabel: 'Shop All Mattresses' },
      { name: 'Prime® Pillow', price: '£89', wasPrice: '', image: '/pillows.webp', link: '/pillows', allLink: '/pillows', allLabel: 'Shop All Pillows' },
      { name: 'Prime® Duvet', price: 'from £139', wasPrice: '', image: '/duvets.webp', link: '/duvets', allLink: '/duvets', allLabel: 'Shop All Duvets' },
    ],
  },
  dealOfWeek: {
    heading: 'Deal of the Week',
  },
  whyPrime: {
    heading: 'Why Prime?',
    description: "No rush to decide — try your mattress at home for 200 nights, with free delivery and returns. Our 10-year guarantee means you can trust in lasting quality.",
    video: '/pm4.mp4',
    icons: [
      { icon: 'Award', label: 'Award winning comfort' },
      { icon: 'Star', label: 'Excellent Reviews' },
      { icon: 'Clock', label: '200-Night Trial' },
      { icon: 'Truck', label: 'Free Delivery & Returns' },
      { icon: 'CreditCard', label: 'Finance available' },
    ],
  },
  customerVideos: {
    heading: 'What Our Customers Are Saying About Us',
    fallbackVideos: [
      { video: '/pm.mp4', title: 'Customer review 1' },
      { video: '/pm4.mp4', title: 'Customer review 2' },
      { video: '/pm.mp4', title: 'Customer review 3' },
    ],
  },
  newsletter: {
    heading: 'Win back the cost of your first order',
  },
};

export const defaultProductPageContent = {
  trustPills: [
    { icon: 'Truck', title: 'Free Delivery', sub: 'UK mainland' },
    { icon: 'Clock', title: '200-Night Trial', sub: 'Risk free' },
    { icon: 'Shield', title: '10-Year Guarantee', sub: 'Full cover' },
  ],
};

export const defaultFooterContent = {
  logo: '/footer-png.png',
  columns: [
    {
      title: 'About',
      links: [
        { label: 'About', url: '/about' },
        { label: 'Reviews', url: '/reviews' },
        { label: 'All Mattresses', url: '/mattresses' },
      ],
    },
    {
      title: 'Support',
      links: [
        { label: 'Contact', url: '/contact' },
        { label: 'FAQ', url: '/faq' },
        { label: 'Free Delivery', url: '/delivery' },
        { label: 'Track Your Order', url: '/track-order' },
      ],
    },
    {
      title: 'The Prime Promise',
      links: [
        { label: 'Top 5 UK Mattresses', url: '' },
        { label: 'Refurbished Mattresses', url: '' },
        { label: '200 Night Trial', url: '' },
        { label: '10 Year Guarantee', url: '' },
      ],
    },
  ],
  countryLabel: '🇬🇧 United Kingdom',
  copyright: 'Copyright © {year} Prime Sleep Ltd. All Rights Reserved. Prime Sleep Limited is registered in England and Wales. Company Number: 12345678. Registered Office: 123 Sleep Street, London, UK.',
};

export const defaultSocialLinks = {
  facebook: 'https://www.facebook.com/profile.php?id=61559839469028',
  instagram: 'https://www.instagram.com/primemattressesofficial/',
  tiktok: 'https://www.tiktok.com/@prime.mattressesoutlet',
  twitter: '',
};

export const FAQ_CATEGORIES = [
  { id: 'delivery', label: 'Delivery' },
  { id: 'returns', label: 'Returns & Trial' },
  { id: 'payment', label: 'Payment' },
  { id: 'product', label: 'Product' },
];
